import { arrayMove } from '@dnd-kit/sortable';
import {
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  SuccessToast,
  createMenuItem,
  deleteMenuTypeItemById,
  noItems,
  updateMenuItem,
  updateMenuItemOrder,
  updateMenuPosItem,
  validateMenuItemDescription,
  validateMenuItemName,
  reportError,
} from '@vgrubs/components';
import {
  Availability,
  CategoryV2Response,
  ItemType,
  MenuItemV2Response,
  MenuPriceCustomizationV2Response,
  MenuV2Response,
  ModifierGroupItemV2Response,
  UpdateMenuListOrders,
} from 'components/src/types/menu.types';
import { ResponseType } from 'components/src/types/shared.types';
import { isAdmin } from 'components/src/helpers/appType';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import {
  MenuTaxRatesModal,
  MenuTaxRatesTarget,
} from '../../taxRates/MenuTaxRatesModal';
import { AddNewModifierGroupModal } from '../Modifiers/AddNewModifierGroupModal';
import { AddItemModal } from './AddItemModal';
import { AddModifierGroupModal } from './AddModifierGroupModal';
import { DeleteItemModal } from './DeleteItemModal';
import { MenuItemTypeComponent } from './MenuItemComponents';
import { ItemPause } from './itemPause.helpers';
import { PauseItemModal } from './PauseItemModal';

export const Items = ({
  allowedToEditMenuPrice,
  menu,
  setMenu,
  setLoading,
  handleChangeShouldPublish,
  isPos,
  timeZone,
}: {
  allowedToEditMenuPrice: boolean;
  menu: MenuV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
  isPos: boolean;
  /** The location's - custom pauses are picked and shown in it. */
  timeZone?: string;
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<{
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  } | null>(null);
  const [pauseModal, setPauseModal] = useState<{
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  } | null>(null);
  const [addModifierGroupModal, setAddModifierGroupModal] = useState(false);
  const [modifierGroups, setModifierGroups] = useState<
    ModifierGroupItemV2Response[]
  >([]);
  const [deleteModal, setDeleteModal] = useState<{
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  } | null>(null);
  const [addNewModifierGroupModal, setAddNewModifierGroupModal] =
    useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [isSorting, setIsSorting] = useState<MenuV2Response | null>(null);
  const [taxRatesModal, setTaxRatesModal] = useState<MenuTaxRatesTarget | null>(
    null
  );

  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const isBrand = brandId !== '0';

  // Admin only, and never on a POS location - the register owns the taxes
  // there and every write answers 400.
  const canEditTaxRates = isAdmin && !isPos;

  async function handleAddItem(
    image: string | Blob | File | null,
    name: string,
    description: string,
    price: number,
    categoryId: string,
    modifierGroups: ModifierGroupItemV2Response[] | null,
    customizedPrices: MenuPriceCustomizationV2Response[] | null,
    emptyForm: () => void,
    availability: Availability,
    preparationNotes: string
  ) {
    const body = {
      name,
      description,
      price,
      pickupPrice: price,
      categoryId,
      modifierGroupIds: modifierGroups?.map((item) => item.id),
      customizedPrices,
      availability,
      preparationNotes,
    };

    if (!name) return ErrorToast('Please enter item name');
    if (!validateMenuItemName(name)) return;
    if (!validateMenuItemDescription(description)) return;

    const formData = new FormData();
    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }
    formData.append('menuItem', JSON.stringify(body));

    try {
      setLoading(true);
      const { data } = await createMenuItem(locationId, brandId, formData);

      emptyForm();
      setMenu(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      setAddModal(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditItem(
    image: string | Blob | File | null,
    name: string,
    description: string,
    price: number,
    categoryId: string,
    modifierGroups: ModifierGroupItemV2Response[] | null,
    existingItem: MenuItemV2Response,
    customizedPrices: MenuPriceCustomizationV2Response[] | null,
    emptyForm: () => void,
    removeImage: boolean | null,
    availability: Availability,
    preparationNotes: string,
    pausedUntilEndOfTheDay: boolean,
    pauseUntil: string | null
  ) {
    const body = {
      name,
      description,
      price,
      pickupPrice: price,
      categoryId,
      modifierGroupIds: modifierGroups?.map((item) => item.id),
      customizedPrices,
      removeImage: removeImage ? removeImage : null,
      availability,
      preparationNotes,
      pauseUntilEndOfTheDay: pausedUntilEndOfTheDay,
      ...(pauseUntil ? { pauseUntil } : {}),
    };

    const posBody: any = {
      description,
      price,
      removeImage: removeImage ? removeImage : null,
      customizedPrices,
    };

    if (isPos && !isBrand) {
      delete posBody.price;
      delete posBody.description;
    }

    if (!name) return ErrorToast('Please enter item name');

    if (!validateMenuItemName(name)) return;
    if (!validateMenuItemDescription(description)) return;

    const formData = new FormData();
    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }
    formData.append('menuItem', JSON.stringify(isPos ? posBody : body));

    try {
      setLoading(true);
      const { data } = await (isPos ? updateMenuPosItem : updateMenuItem)(
        locationId,
        brandId,
        existingItem.id,
        formData
      );
      emptyForm();
      setMenu(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      setEditModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Pause or resume from the row. The update route takes the whole item, so
   * the body is the item as it stands with only its availability changed.
   */
  async function handleChangeAvailability(
    { item, category }: NonNullable<typeof pauseModal>,
    pause: ItemPause
  ) {
    const body = {
      name: item.name,
      description: item.description ?? '',
      price: item.price,
      pickupPrice: item.price,
      categoryId: category.id,
      modifierGroupIds: item.modifierGroups?.map((group) => group.id),
      customizedPrices: item.customizedPrices,
      removeImage: null,
      preparationNotes: item.preparationNotes ?? '',
      ...pause,
    };

    const formData = new FormData();
    formData.append('menuItem', JSON.stringify(body));

    try {
      setLoading(true);
      const { data } = await updateMenuItem(
        locationId,
        brandId,
        item.id,
        formData
      );
      setMenu(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      setPauseModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteItem() {
    if (!deleteModal) return;
    try {
      setLoading(true);
      const { data } = await deleteMenuTypeItemById(
        locationId,
        brandId,
        deleteModal.item.id,
        ItemType.Item
      );

      setMenu(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      setDeleteModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const AddItemButton = () => {
    return (
      <Button $selected onClick={() => setAddModal(true)}>
        Add Item
      </Button>
    );
  };

  function dragEndEvent(e: any, categoryId: number) {
    const { over, active } = e;
    moveItem({ over, active, categoryId });
  }

  const moveItem = ({
    over,
    active,
    categoryId,
  }: {
    over: { id: number };
    active: { id: number };
    categoryId: number;
  }) => {
    const category = menu?.categories?.find((cat) => cat.id === categoryId);
    if (!category?.items) return;

    const sortedItems = arrayMove(
      category.items,
      category.items.findIndex((item) => item.id === active.id),
      category.items.findIndex((item) => item.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        categories: prev.categories.map((cat) =>
          cat.id === categoryId ? { ...cat, items: sortedItems } : cat
        ),
      };
    });
  };

  function handleDragStart(event: any, categoryId: number) {
    setActiveId(event.active.id);
    setActiveCategoryId(categoryId);
  }

  function handleDragEnd(event: any, categoryId: number) {
    if (event.over) {
      setActiveId(null);
      setActiveCategoryId(null);
      dragEndEvent(event, categoryId);
    }
  }

  async function handleSaveOrder() {
    try {
      setLoading(true);
      if (!menu?.categories) return;

      let lastData: ResponseType<MenuV2Response> | null = null;
      for (const category of menu.categories) {
        if (category.items && category.items.length > 0) {
          const list: UpdateMenuListOrders[] = category.items.map(
            (item, index) => ({
              id: item.id,
              order: index + 1,
            })
          );
          const { data } = await updateMenuItemOrder(
            locationId,
            brandId,
            category.id,
            list
          );
          lastData = data;
        }
      }

      if (lastData) {
        setMenu(lastData.data);
        SuccessToast(lastData.message);
      }
      setLoading(false);
      handleChangeShouldPublish(true);
      setIsSorting(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const totalChildrenCount = menu?.categories?.reduce((count, category) => {
    return count + (category.items ? category.items.length : 0);
  }, 0);

  const SortingButtons = () => {
    return (
      <>
        {isSorting && (
          <Button
            $selected
            $small
            $gray
            $w="130px"
            $wSM="calc(50% - 8px)"
            onClick={() => {
              setMenu(isSorting);
              setIsSorting(null);
            }}
          >
            Cancel Sorting
          </Button>
        )}
        <Button
          $selected={!!isSorting}
          $small
          $w="130px"
          $wSM={!isSorting ? '100%' : 'calc(50% - 8px)'}
          onClick={() => {
            if (!!isSorting) {
              return handleSaveOrder();
            }
            setIsSorting(menu);
          }}
        >
          {isSorting ? 'Save Sorting' : 'Sort Items'}
        </Button>
      </>
    );
  };

  return (
    <Div>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Items {menu?.categories && `(${totalChildrenCount})`}</H3>
        <Div $flex $alignCenter $gap="16px">
          <Div $flex $gap="16px" $desktop>
            <SortingButtons />
          </Div>

          {!isPos && menu?.categories && menu?.categories?.length > 0 && (
            <Icon $pointer onClick={() => setAddModal(true)}>
              <PlusIcon />
            </Icon>
          )}
        </Div>
      </Div>

      <Div $flex $gap="16px" $mobile $mb="20">
        <SortingButtons />
      </Div>

      {menu?.categories?.map((item, i) => (
        <MenuItemTypeComponent
          key={item.id}
          item={item}
          onClick={(item) => setEditModal(item)}
          onClickDelete={(id) => setDeleteModal(id)}
          // A POS location's register owns availability.
          onClickPause={isPos ? undefined : (data) => setPauseModal(data)}
          onClickResume={
            isPos
              ? undefined
              : (data) =>
                  handleChangeAvailability(data, {
                    availability: Availability.Available,
                    pauseUntilEndOfTheDay: false,
                  })
          }
          timeZone={timeZone}
          menu={menu}
          isSorting={!!isSorting}
          activeId={activeId}
          activeCategoryId={activeCategoryId}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          isPos={isPos}
          handleEditTaxRates={
            canEditTaxRates
              ? (menuItem) =>
                  setTaxRatesModal({
                    kind: 'item',
                    id: menuItem.id,
                    name: menuItem.name,
                    taxRates: menuItem.taxRates ?? [],
                    taxRatesOverridden: !!menuItem.taxRatesOverridden,
                  })
              : undefined
          }
        />
      ))}

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noItems} $mb="24" />
          <H3 $mb="24" $center>
            No Items
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu items.
          </P>
          {!isPos && <AddItemButton />}
        </Div>
      )}

      {/* {menu?.categories && menu?.categories?.length > 0 && (
        <>
          <Div
            $mobile
            style={{
              position: 'fixed',
              right: '30px',
              bottom: '30px',
              left: '30px',
            }}
            $flex
            $justifyEnd
            $mt="40"
          >
            <AddItemButton />
          </Div>
          <Div
            $desktop
            style={{ position: 'fixed', right: '50px', bottom: '50px' }}
            $flex
            $justifyEnd
            $mt="40"
          >
            <AddItemButton />
          </Div>
        </>
      )} */}

      <AddItemModal
        menu={menu}
        setMenu={setMenu}
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleAddItem={handleAddItem}
        isPos={isPos}
        isBrand={isBrand}
        allowedToEditMenuPrice={allowedToEditMenuPrice}
        handleChangeShouldPublish={handleChangeShouldPublish}
        setLoading={setLoading}
      />

      <AddItemModal
        allowedToEditMenuPrice={allowedToEditMenuPrice}
        menu={menu}
        setMenu={setMenu}
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleEditItem={handleEditItem}
        timeZone={timeZone}
        edit
        isPos={isPos}
        isBrand={isBrand}
        handleChangeShouldPublish={handleChangeShouldPublish}
        setLoading={setLoading}
      />

      <PauseItemModal
        item={pauseModal?.item ?? null}
        timeZone={timeZone}
        closeModal={() => setPauseModal(null)}
        handlePause={(pause) =>
          pauseModal && handleChangeAvailability(pauseModal, pause)
        }
      />
      <AddModifierGroupModal
        addModifierGroupModal={addModifierGroupModal}
        closeModal={() => {
          setAddModifierGroupModal(false);
        }}
        menu={menu}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        setAddNewModifierGroupModal={setAddNewModifierGroupModal}
      />

      <DeleteItemModal
        deleteModal={!!deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteItem={handleDeleteItem}
      />

      <AddNewModifierGroupModal
        addNewModal={addNewModifierGroupModal}
        closeModal={() => {
          setAddNewModifierGroupModal(false);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <MenuTaxRatesModal
        target={taxRatesModal}
        closeModal={() => {
          setTaxRatesModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />
    </Div>
  );
};
